import React, { useState } from 'react';
import { Table, Container, Navbar, Nav, Form, Button, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  useGetFulfillmentQueueQuery,
  useMarkShippedMutation,
  useMarkDeliveredMutation,
} from '../../slices/ordersApiSlice';
import Message from '../../components/Message';
import Loader from '../../components/Loader';

const CARRIERS = ['SMSA', 'Aramex', 'DHL', 'SPL', 'Saudi Post'];

function ShippmentsScreen() {
  const { data, isLoading, error, refetch } = useGetFulfillmentQueueQuery();
  const [markShipped, { isLoading: shipping }] = useMarkShippedMutation();
  const [markDelivered] = useMarkDeliveredMutation();

  // Per-row carrier + AWB input state, keyed by order id.
  const [rows, setRows] = useState({});
  const setRow = (id, field, value) =>
    setRows((r) => ({ ...r, [id]: { ...r[id], [field]: value } }));

  const orders = data?.orders || [];

  const shipHandler = async (id) => {
    const carrier = rows[id]?.carrier || 'SMSA';
    const awb = (rows[id]?.awb || '').trim();
    if (!awb) {
      toast.error('Enter a tracking number (AWB) first.');
      return;
    }
    try {
      await markShipped({ order_id: id, carrier, awb }).unwrap();
      toast.success(`Order #${id} marked shipped — customer notified.`);
    } catch (e) {
      toast.error(e?.data?.message || 'Failed to mark shipped.');
    }
  };

  const deliveredHandler = async (id) => {
    try {
      await markDelivered({ order_id: id }).unwrap();
      toast.success(`Order #${id} marked delivered.`);
    } catch (e) {
      toast.error(e?.data?.message || 'Failed to mark delivered.');
    }
  };

  return (
    <Container dir='ltr'>
      <Navbar bg='light' expand='lg' className='mb-5'>
        <Navbar.Toggle aria-controls='basic-navbar-nav' />
        <Navbar.Collapse id='basic-navbar-nav'>
          <Nav className='mr-auto'>
            <Nav.Link><Link to={'/admin/orderlist'}>orders</Link></Nav.Link>
            <Nav.Link><Link to={'/admin/receipts'}>Bank Receipts</Link></Nav.Link>
            <Nav.Link><Link to={'/admin/contacts'}>Contact Requests</Link></Nav.Link>
            <Nav.Link><Link to={'/admin/newsletters'}>Newsletter Subscribtions</Link></Nav.Link>
            <Nav.Link active><Link to={'/admin/shippments'}>Shippments</Link></Nav.Link>
          </Nav>
        </Navbar.Collapse>
      </Navbar>

      <div className='d-flex align-items-center justify-content-between mb-3'>
        <h1 className='m-0'>Fulfillment queue</h1>
        <Button variant='outline-secondary' size='sm' onClick={refetch}>Refresh</Button>
      </div>
      <p className='text-muted'>Paid orders awaiting shipment. Enter the carrier and tracking number, then mark shipped — the customer is emailed automatically (and messaged on WhatsApp for WhatsApp orders).</p>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant='danger'>{error?.data?.message || error.error}</Message>
      ) : orders.length === 0 ? (
        <Message variant='success'>Nothing waiting to ship — the queue is clear. 🌴</Message>
      ) : (
        <Table striped bordered hover responsive className='table-sm align-middle'>
          <thead>
            <tr>
              <th>Order</th>
              <th>Customer</th>
              <th>Destination</th>
              <th>Items</th>
              <th>Total</th>
              <th>Carrier</th>
              <th>Tracking #</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id}>
                <td>
                  #{o.id}
                  {o.source === 'whatsapp' && (
                    <> <Badge bg='success'>WhatsApp</Badge></>
                  )}
                  <div className='text-muted small'>{o.created_at ? o.created_at.substring(0, 10) : ''}</div>
                </td>
                <td>
                  {o.name || '—'}
                  <div className='text-muted small'>{o.phone}</div>
                </td>
                <td>
                  {o.city}
                  <div className='text-muted small'>{o.country}</div>
                </td>
                <td>
                  {(o.items || []).map((it, i) => (
                    <div key={i} className='small'>{it.name} ×{it.qty}</div>
                  ))}
                  <div className='text-muted small'>{o.weight} kg</div>
                </td>
                <td>{o.totalPrice} sar</td>
                <td style={{ minWidth: 120 }}>
                  <Form.Select
                    size='sm'
                    value={rows[o.id]?.carrier || 'SMSA'}
                    onChange={(e) => setRow(o.id, 'carrier', e.target.value)}
                  >
                    {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </Form.Select>
                </td>
                <td style={{ minWidth: 150 }}>
                  <Form.Control
                    size='sm'
                    placeholder='AWB / tracking no.'
                    value={rows[o.id]?.awb || ''}
                    onChange={(e) => setRow(o.id, 'awb', e.target.value)}
                  />
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <Button size='sm' variant='success' disabled={shipping} onClick={() => shipHandler(o.id)}>
                    Mark shipped
                  </Button>{' '}
                  <Button size='sm' variant='outline-secondary' onClick={() => deliveredHandler(o.id)}>
                    Delivered
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Container>
  );
}

export default ShippmentsScreen;
